import { useEffect, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import {
  Alert,
  Box,
  Button,
  Card,
  Checkbox,
  Chip,
  Divider,
  FormControl,
  FormLabel,
  Input,
  LinearProgress,
  Modal,
  ModalClose,
  ModalDialog,
  Option,
  Select,
  Stack,
  Textarea,
  Typography,
} from "@mui/joy";
import {
  CheckCircle,
  ClipboardText,
  EnvelopeSimple,
  MagnifyingGlass,
} from "@phosphor-icons/react";
import {
  assessmentApi as api,
  errorText,
  statusLabel,
  type AssessmentCampaign,
  type AssessmentQuestion,
  type AssessmentReport,
  type AssessmentSummary,
  type Ratings,
} from "./assessmentApi";

export function AssessmentScore({
  attempt,
}: {
  attempt: Pick<AssessmentSummary, "status" | "result">;
}) {
  const value = attempt.result.score;
  return (
    <Chip
      size="md"
      color={value !== null && value !== undefined ? "primary" : "neutral"}
      variant="soft"
    >
      {value !== null && value !== undefined
        ? `Assessment ${value}/100`
        : statusLabel(attempt.status)}
    </Chip>
  );
}
function displayAnswer(
  q: AssessmentQuestion,
  value: AssessmentReport["answers"][string] | undefined,
) {
  if (value === undefined || value === "") return "Not answered";
  return q.options?.length
    ? q.options
        .filter((o) =>
          Array.isArray(value) ? value.includes(o.id) : value === o.id,
        )
        .map((o) => o.label)
        .join(", ")
    : String(value);
}
function RubricReview({
  q,
  value,
  onConfirm,
}: {
  q: AssessmentQuestion;
  value?: Ratings[string];
  onConfirm: (rating: Ratings[string]) => void;
}) {
  const [criteria, setCriteria] = useState(
    value?.criteria || q.rubric.map(() => 0),
  );
  const [notes, setNotes] = useState(value?.notes || "");
  return (
    <Card variant="soft">
      <Typography level="title-sm">
        Evidence-based review · {q.max_points} points
      </Typography>
      <Typography level="body-sm">
        Check only criteria demonstrated in the answer, then confirm your
        rating. Unchecked criteria receive no points.
      </Typography>
      {q.rubric.map((label, i) => (
        <Checkbox
          key={i}
          label={label}
          checked={criteria[i] === 1}
          onChange={(e) =>
            setCriteria(
              criteria.map((v, n) => (n === i ? Number(e.target.checked) : v)),
            )
          }
        />
      ))}
      <FormControl>
        <FormLabel>Evidence / reviewer notes</FormLabel>
        <Textarea
          minRows={2}
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
        />
      </FormControl>
      <Button
        variant="outlined"
        startDecorator={<CheckCircle />}
        onClick={() => onConfirm({ criteria, notes })}
      >
        {value ? "Update confirmed rating" : "Confirm rating"} ·{" "}
        {Math.round(
          (criteria.reduce((n, v) => n + v, 0) / q.rubric.length) *
            q.max_points *
            10,
        ) / 10}{" "}
        / {q.max_points}
      </Button>
    </Card>
  );
}

function ReportBody({
  report,
  onChanged,
  onDirty,
}: {
  report: AssessmentReport;
  onDirty: (dirty: boolean) => void;
  onChanged: (r: AssessmentReport) => void;
}) {
  const [ratings, setRatings] = useState<Ratings>(
    structuredClone(report.ratings),
  );
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const reviewed = ["submitted", "reviewed"].includes(report.status);
  const dirty = JSON.stringify(ratings) !== JSON.stringify(report.ratings);
  useEffect(() => {
    onDirty(dirty);
  }, [dirty, onDirty]);
  const written = report.definition.sections
    .flatMap((s) => s.questions)
    .filter((q) => q.type === "text" && q.max_points > 0);
  async function act(operation: string) {
    setBusy(true);
    setError("");
    setNotice("");
    try {
      const next = await api.post<AssessmentReport>(
        `assessment-attempts/${report.id}/`,
        { operation, revision: report.revision, ratings, reason },
      );
      onChanged(next);
      if (operation === "invite")
        setNotice(
          next.delivery_accepted
            ? "Resume email queued. Check delivery history for its status."
            : "Delivery could not be confirmed. Check delivery history before trying again.",
        );
      else if (operation === "retake")
        setNotice(
          "Retake created. Send its resume link when ready. The original remains selected until you choose a fully reviewed replacement.",
        );
      else setNotice("Saved. Hiring stage has not been changed.");
    } catch (e) {
      setError(errorText(e));
    } finally {
      setBusy(false);
    }
  }
  return (
    <Stack spacing={2}>
      <Box>
        <Typography level="h3">{report.name}</Typography>
        <Typography>{report.email}</Typography>
        <Typography level="body-sm">
          {report.title} · Version {report.version_number} · Started{" "}
          {new Date(report.started_at).toLocaleDateString()}
        </Typography>
      </Box>
      <Card variant="soft">
        <Stack
          direction={{xs: "column", sm: "row"}}
          sx={{ gap: 2, alignItems: {xs: "stretch", sm: "center"}, flexWrap: "wrap" }}
        >
          <Box>
            <Typography level="body-sm">Assessment score</Typography>
            <Typography level="h1">
              {report.result.score ?? "—"}
              {report.result.score !== null && (
                <Typography level="body-lg"> / 100</Typography>
              )}
            </Typography>
          </Box>
          <Box sx={{ flex: 1 }}>
            <Chip>{statusLabel(report.status)}</Chip>
            <Typography sx={{ mt: 1 }}>
              {reviewed
                ? `${report.result.reviewed} of ${report.result.scored_questions} scored answers reviewed · ${report.result.pending} written answer(s) need review`
                : `${report.result.answered} of ${report.result.total} answers saved. No score until submission and review.`}
            </Typography>
            <Typography level="body-sm">
              {report.selected
                ? "Selected attempt for this candidate"
                : "Historical / alternate attempt"}
              . This is job-sample evidence, not a hiring decision or a
              prediction of performance.
            </Typography>
          </Box>
        </Stack>
      </Card>
      {error && <Alert color="danger">{error}</Alert>}
      {notice && <Alert color="success">{notice}</Alert>}
      {!!report.result.flags.length && (
        <Alert color="warning">
          {report.result.flags.length} answer(s) flagged for discussion. Read
          the answer and clarify with the candidate; flags never automatically
          reject an application.
        </Alert>
      )}
      {reviewed && (
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "1fr", sm: "repeat(3, 1fr)" },
            gap: 1,
          }}
        >
          {report.result.sections.map((s) => (
            <Card key={s.id}>
              <Typography level="title-md">{s.title}</Typography>
              <Typography>
                {s.weight
                  ? s.score === null
                    ? "Review pending"
                    : `${s.score}%`
                  : "Informational"}
              </Typography>
              <Typography level="body-sm">
                {s.weight
                  ? `${s.weight}% of overall score`
                  : "Does not affect score"}
              </Typography>
            </Card>
          ))}
        </Box>
      )}
      <Typography level="h4">Answers & evidence</Typography>
      {report.definition.sections.map((s) => (
        <Box key={s.id}>
          <Typography level="title-lg" sx={{ mb: 1 }}>
            {s.title}
          </Typography>
          <Stack spacing={1}>
            {s.questions
              .filter((q) => !q.condition || report.answers[q.id] !== undefined)
              .map((q) => {
                const result = report.result.questions.find(
                  (v) => v.id === q.id,
                );
                return (
                  <Box
                    key={q.id}
                    component="details"
                    open={
                      reviewed &&
                      q.type === "text" &&
                      q.max_points > 0 &&
                      !ratings[q.id]
                    }
                    sx={{
                      border: "1px solid",
                      borderColor: "divider",
                      borderRadius: "md",
                      p: 1.5,
                    }}
                  >
                    <Box component="summary" sx={{ cursor: "pointer", py: 1 }}>
                      {q.prompt}{" "}
                      {result?.flag && (
                        <Chip color="warning" size="sm">
                          Discuss
                        </Chip>
                      )}{" "}
                      {q.max_points > 0 && (
                        <Chip size="sm" variant="soft">
                          {result?.points === null ||
                          result?.points === undefined
                            ? "Needs review"
                            : `${result.points} / ${q.max_points}`}
                        </Chip>
                      )}
                    </Box>
                    <Stack spacing={2} sx={{ pt: 2 }}>
                      <Typography
                        sx={{
                          whiteSpace: "pre-wrap",
                          overflowWrap: "anywhere",
                        }}
                      >
                        {displayAnswer(q, report.answers[q.id])}
                      </Typography>
                      {q.competency && (
                        <Typography level="body-sm">
                          Skill: {q.competency}
                        </Typography>
                      )}
                      {reviewed && q.type === "text" && q.max_points > 0 ? (
                        <RubricReview
                          q={q}
                          value={ratings[q.id]}
                          onConfirm={(rating) =>
                            setRatings({ ...ratings, [q.id]: rating })
                          }
                        />
                      ) : (
                        q.max_points > 0 && (
                          <Typography level="body-sm">
                            {q.type === "number"
                              ? `Answer key: ${q.answer} (rounding tolerance ${q.tolerance})`
                              : `Full-credit answer: ${q.options
                                  .filter((o) => o.points === q.max_points)
                                  .map((o) => o.label)
                                  .join("; ")}`}
                          </Typography>
                        )
                      )}
                    </Stack>
                  </Box>
                );
              })}
          </Stack>
        </Box>
      ))}
      {reviewed && (
        <Card
          sx={{ position: dirty ? "sticky" : "static", bottom: 0, zIndex: 2 }}
        >
          <Typography level="title-md">
            {written.filter((q) => !!ratings[q.id]).length} of {written.length}{" "}
            written ratings confirmed
          </Typography>
          <FormControl>
            <FormLabel>
              Reason for review changes / selecting a retake
            </FormLabel>
            <Textarea
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="Required when changing a saved review, offering a retake, or selecting another attempt"
            />
          </FormControl>
          <Button
            loading={busy}
            disabled={!dirty}
            onClick={() => act("review")}
          >
            {dirty
              ? "Save review & update score"
              : Object.keys(ratings).length
                ? "Review saved"
                : "Confirm ratings above"}
          </Button>
        </Card>
      )}
      <Box component="details">
        <Box component="summary" sx={{ cursor: "pointer" }}>
          Interview guide
        </Box>
        <Stack spacing={1} sx={{ mt: 2 }}>
          {report.definition.interview.map((prompt, i) => (
            <Typography key={i}>
              {i + 1}. {prompt}
            </Typography>
          ))}
        </Stack>
      </Box>
      <Divider />
      <Typography level="title-lg">Attempt actions</Typography>
      <Stack direction="row" sx={{ flexWrap: "wrap", gap: 1 }}>
        {!reviewed && report.status !== "withdrawn" && (
          <Button
            variant="outlined"
            startDecorator={<EnvelopeSimple />}
            loading={busy}
            onClick={() => {
              if (
                window.confirm(`Send a private resume link to ${report.email}?`)
              )
                void act("invite");
            }}
          >
            Send resume link
          </Button>
        )}
        {reviewed && (
          <Button
            variant="outlined"
            disabled={busy || !reason.trim()}
            onClick={() => act("retake")}
          >
            Offer retake
          </Button>
        )}
        {report.status === "reviewed" && !report.selected && (
          <Button
            variant="outlined"
            disabled={busy || !reason.trim()}
            onClick={() => act("select")}
          >
            Use this reviewed attempt
          </Button>
        )}
      </Stack>
      <Typography level="body-sm">
        Resume links expire after 30 days. At most one email is attempted per
        day; completed, withdrawn and closed applications cannot receive
        reminders. Reminders are sent only when requested.
      </Typography>
      <Box component="details">
        <Box component="summary" sx={{ cursor: "pointer" }}>
          Review & delivery history
        </Box>
        <Stack spacing={1} sx={{ mt: 2 }}>
          {report.events.map((e, i) => (
            <Card key={i} variant="soft">
              <Typography level="title-sm">
                {e.kind.replaceAll("_", " ")}
              </Typography>
              <Typography level="body-sm">
                {new Date(e.created_at).toLocaleString()} ·{" "}
                {[e.actor__first_name, e.actor__last_name]
                  .filter(Boolean)
                  .join(" ") || "Candidate / system"}
              </Typography>
              {typeof e.data.reason === "string" && (
                <Typography>{e.data.reason}</Typography>
              )}
            </Card>
          ))}
        </Stack>
      </Box>
    </Stack>
  );
}

export function AssessmentReportDialog({
  id,
  onClose,
}: {
  id: string;
  onClose: () => void;
}) {
  const qc = useQueryClient();
  const [activeId, setActiveId] = useState(id);
  const [dirty, setDirty] = useState(false);
  const close = () => {
    if (!dirty || window.confirm("Discard your unsaved assessment ratings?"))
      onClose();
  };
  useEffect(() => {
    const guard = (event: BeforeUnloadEvent) => {
      if (dirty) {
        event.preventDefault();
        event.returnValue = "";
      }
    };
    window.addEventListener("beforeunload", guard);
    return () => window.removeEventListener("beforeunload", guard);
  }, [dirty]);
  const query = useQuery({
    queryKey: ["assessment-report", activeId],
    queryFn: () =>
      api.get<AssessmentReport>(`assessment-attempts/${activeId}/`),
  });
  return (
    <Modal open onClose={close}>
      <ModalDialog
        sx={{
          width: "min(960px, 96vw)",
          maxHeight: "94vh",
          overflowY: "auto",
          p: { xs: 2, sm: 3 },
        }}
      >
        <ModalClose aria-label="Close assessment dialog" />
        <Box sx={{ pt: 2 }}>
          {query.isLoading ? (
            <LinearProgress />
          ) : query.error ? (
            <Alert color="danger">
              Unable to load assessment.{" "}
              <Button onClick={() => void query.refetch()}>Retry</Button>
            </Alert>
          ) : (
            query.data && (
              <ReportBody
                key={`${query.data.id}:${query.data.revision}`}
                report={query.data}
                onDirty={setDirty}
                onChanged={(next) => {
                  qc.setQueryData(["assessment-report", next.id], next);
                  setActiveId(next.id);
                  void qc.invalidateQueries({
                    queryKey: ["assessment-results"],
                  });
                  void qc.invalidateQueries({ queryKey: ["candidates"] });
                  void qc.invalidateQueries({
                    queryKey: ["campaign-candidates"],
                  });
                  void qc.invalidateQueries({ queryKey: ["candidate"] });
                }}
              />
            )
          )}
        </Box>
      </ModalDialog>
    </Modal>
  );
}

function Comparison({
  rows,
  onClose,
}: {
  rows: AssessmentSummary[];
  onClose: () => void;
}) {
  const comparable = new Set(rows.map((r) => r.version_id)).size === 1;
  return (
    <Modal open onClose={onClose}>
      <ModalDialog
        sx={{ width: "min(1100px, 96vw)", maxHeight: "90vh", overflow: "auto" }}
      >
        <ModalClose aria-label="Close assessment dialog" />
        <Typography level="h3">Compare assessment evidence</Typography>
        {!comparable && (
          <Alert color="warning">
            Different assessment versions. These scores are not directly
            comparable; review each candidate’s underlying evidence.
          </Alert>
        )}
        <Typography>
          Compare the same role and version. Scores support your interview; they
          do not decide who gets hired.
        </Typography>
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: {
              xs: "1fr",
              md: `repeat(${rows.length}, minmax(0, 1fr))`,
            },
            gap: 2,
          }}
        >
          {rows.map((r) => (
            <Card key={r.id}>
              <Typography level="title-lg">{r.name}</Typography>
              <AssessmentScore attempt={r} />
              <Typography level="body-sm">
                {r.title} · v{r.version_number}
              </Typography>
              {r.result.sections.map((s) => (
                <Box key={s.id}>
                  <Typography level="title-sm">{s.title}</Typography>
                  <Typography>
                    {s.weight
                      ? s.score === null
                        ? "Review pending"
                        : `${s.score}% · weight ${s.weight}%`
                      : "Informational"}
                  </Typography>
                </Box>
              ))}
              <Typography>
                {r.result.flags.length} discussion flag(s)
              </Typography>
              <Typography>
                {r.selected ? "Selected attempt" : "Alternate attempt"}
              </Typography>
            </Card>
          ))}
        </Box>
      </ModalDialog>
    </Modal>
  );
}

export function AssessmentResults({
  campaignId,
  candidateId,
}: {
  campaignId: string;
  candidateId?: string;
}) {
  const [assigning, setAssigning] = useState(false);
  const [assignError, setAssignError] = useState("");
  const [version, setVersion] = useState("");
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState("");
  const [order, setOrder] = useState("recent");
  const [page, setPage] = useState(1);
  const [flags, setFlags] = useState(false);
  const [selected, setSelected] = useState<AssessmentSummary[]>([]);
  const [compare, setCompare] = useState(false);
  const [report, setReport] = useState("");
  const query = useQuery({
    queryKey: [
      "assessment-results",
      campaignId,
      version,
      candidateId,
      search,
      status,
      order,
      page,
      flags,
    ],
    queryFn: () =>
      api.get<AssessmentCampaign>(
        `campaigns/${campaignId}/assessment/?${new URLSearchParams({ search, version, status, order, page: String(page), flags: String(flags), ...(candidateId ? { candidate: candidateId } : {}) })}`,
      ),
  });
  if (query.isLoading)
    return (
      <Card>
        <Typography>Loading assessments…</Typography>
        <LinearProgress />
      </Card>
    );
  if (query.error)
    return (
      <Alert color="danger">
        Could not load assessments.{" "}
        <Button onClick={() => void query.refetch()}>Retry</Button>
      </Alert>
    );
  const data = query.data!;
  const counts = data.counts;
  const total = Object.values(counts).reduce((a, b) => a + b, 0);
  const submitted = (counts.submitted || 0) + (counts.reviewed || 0);
  return (
    <Stack spacing={2}>
      <Stack
        direction="row"
        sx={{ justifyContent: "space-between", flexWrap: "wrap", gap: 1 }}
      >
        <Box>
          <Typography
            level={candidateId ? "title-lg" : "h3"}
            startDecorator={<ClipboardText />}
          >
            Assessments
          </Typography>
          <Typography level="body-sm">
            {candidateId
              ? "Review current and previous attempts."
              : "See progress, review written answers, and compare job-related evidence."}
          </Typography>
        </Box>
        <Button
          variant="outlined"
          onClick={() => {
            setSelected([]);
            void query.refetch();
          }}
        >
          Refresh
        </Button>
      </Stack>
      {!candidateId && (
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "repeat(2, 1fr)", md: "repeat(4, 1fr)" },
            gap: 1,
          }}
        >
          {[
            ["Registered", total],
            ["Submitted", submitted],
            ["Needs review", counts.submitted || 0],
            [
              "Completion",
              total ? `${Math.round((submitted / total) * 100)}%` : "—",
            ],
          ].map(([label, value]) => (
            <Card key={label}>
              <Typography level="body-sm">{label}</Typography>
              <Typography level="h3">{value}</Typography>
            </Card>
          ))}
        </Box>
      )}
      {!candidateId && data.versions?.length > 1 && !version && <Alert color="warning">This campaign includes different assessment versions. Choose one version to compare scores consistently.</Alert>}
      {!candidateId && (
        <Typography level="body-xs">
          All assessment attempts in this campaign, including retakes.
          Completion means submitted, not hired. Unfinished attempts have no
          score.
        </Typography>
      )}
      {!candidateId && (
        <Stack direction={{ xs: "column", md: "row" }} sx={{ gap: 1 }}>
          <Input
            sx={{ flex: 1 }}
            startDecorator={<MagnifyingGlass />}
            placeholder="Find a candidate…"
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
              setPage(1);
            }}
            slotProps={{
              input: { "aria-label": "Search assessment candidates" },
            }}
          />
          <Select
            aria-label="Assessment status"
            value={status}
            onChange={(_, v) => {
              setStatus(v || "");
              setPage(1);
            }}
          >
            <Option value="">All progress</Option>
            {[
              "registered",
              "in_progress",
              "submitted",
              "reviewed",
              "withdrawn",
            ].map((v) => (
              <Option key={v} value={v}>
                {statusLabel(v)}
              </Option>
            ))}
          </Select>
          {data.versions?.length > 1 && <Select aria-label="Assessment version" value={version} onChange={(_,value) => {setVersion(value || ''); setPage(1); setSelected([]);}}><Option value="">All versions</Option>{data.versions.map(v => <Option key={v.version_id} value={String(v.version_id)}>{v.version__definition__title} · v{v.version__number}</Option>)}</Select>}
          <Select
            aria-label="Sort assessments"
            value={order}
            onChange={(_, v) => {
              setOrder(v || "recent");
              setPage(1);
            }}
          >
            <Option value="recent">Most recent</Option>
            <Option value="score">Highest reviewed score</Option>
          </Select>
        </Stack>
      )}
      {!candidateId && (
        <Stack
          direction="row"
          sx={{ gap: 2, flexWrap: "wrap", alignItems: "center" }}
        >
          <Checkbox
            label="Discussion flags"
            checked={flags}
            onChange={(e) => {
              setFlags(e.target.checked);
              setPage(1);
            }}
          />
          <Button
            variant="outlined"
            disabled={selected.length < 2}
            onClick={() => setCompare(true)}
          >
            Compare {selected.length ? `(${selected.length}/4)` : "candidates"}
          </Button>
          {selected.length > 0 && (
            <Button variant="plain" onClick={() => setSelected([])}>
              Clear selection
            </Button>
          )}
        </Stack>
      )}
      {assignError && <Alert color="danger">{assignError}</Alert>}
      {candidateId && data.version_id && !data.attempts.length && (
        <Button
          variant="outlined"
          loading={assigning}
          onClick={async () => {
            setAssigning(true);
            setAssignError("");
            try {
              const assigned = await api.post<AssessmentReport>(
                `campaigns/${campaignId}/assessment/`,
                { candidate_id: candidateId },
              );
              setReport(assigned.id);
              await query.refetch();
            } catch (e) {
              setAssignError(errorText(e));
            } finally {
              setAssigning(false);
            }
          }}
        >
          Create assessment for this candidate
        </Button>
      )}
      {!data.attempts.length && (
        <Card variant="soft">
          <Typography level="title-md">No assessments here yet</Typography>
          <Typography>
            {data.version_id
              ? "Candidates will appear after saving their registration. Try another filter if you expected a result."
              : "Enable a published template in campaign settings → Assessment setup to get started."}
          </Typography>
        </Card>
      )}
      {data.attempts.map((row) => (
        <Card key={row.id}>
          <Stack
            direction={candidateId ? "column" : { xs: "column", sm: "row" }}
            sx={{
              gap: 2,
              alignItems: candidateId ? "stretch" : { sm: "center" },
            }}
          >
            <Stack
              direction="row"
              spacing={1.5}
              sx={{
                alignItems: "center",
                minWidth: candidateId ? 0 : { sm: 240 },
              }}
            >
              {!candidateId && (
                <Checkbox
                  slotProps={{ input: { "aria-label": `Compare ${row.name}` } }}
                  checked={selected.some((r) => r.id === row.id)}
                  disabled={
                    !selected.some((r) => r.id === row.id) &&
                    selected.length === 4
                  }
                  onChange={(e) =>
                    setSelected(
                      e.target.checked
                        ? [...selected, row]
                        : selected.filter((r) => r.id !== row.id),
                    )
                  }
                />
              )}
              <Box sx={{ minWidth: 0 }}>
                <Typography level="title-md">{row.name}</Typography>
                <Typography level="body-sm" sx={{ overflowWrap: "anywhere" }}>
                  {row.email}
                </Typography>
              </Box>
            </Stack>
            <Box sx={{ flex: 1 }}>
              <AssessmentScore attempt={row} />
              <Typography level="body-sm" sx={{ mt: 0.5 }}>
                {row.result.score === null
                  ? `${row.result.answered}/${row.result.total} answers${row.status === "submitted" ? ` · ${row.result.pending} written reviews pending` : " · Not scored yet"}`
                  : `${row.result.flags.length} discussion flags`}{" "}
                · v{row.version_number}
                {row.selected ? "" : " · Alternate attempt"}
              </Typography>
            </Box>
            <Button variant="outlined" onClick={() => setReport(row.id)}>
              {row.status === "submitted"
                ? "Review answers"
                : row.status === "reviewed"
                  ? "View score & evidence"
                  : "View progress"}
            </Button>
          </Stack>
        </Card>
      ))}
      {data.count > 30 && (
        <Stack direction="row" spacing={2}>
          <Button disabled={page <= 1} onClick={() => setPage(page - 1)}>
            Previous
          </Button>
          <Typography>
            Page {page} · {data.count} attempts
          </Typography>
          <Button
            disabled={page * 30 >= data.count}
            onClick={() => setPage(page + 1)}
          >
            Next
          </Button>
        </Stack>
      )}
      {report && (
        <AssessmentReportDialog id={report} onClose={() => setReport("")} />
      )}
      {compare && (
        <Comparison rows={selected} onClose={() => setCompare(false)} />
      )}
    </Stack>
  );
}
